// CAT Mock 2 — VARC section.
const catMock2Varc = [
  {
    id: 1,
    type: "mcq",
    passageId: "rc1",
    passage: `Histories of technology tend to be histories of novelty. We remember the inventor of the steam engine, the first transatlantic cable and the launch of the first smartphone; we rarely remember the people who kept those things running. Yet for most of the life of any technology, the dominant activity surrounding it is not invention but maintenance. A bridge is designed once and inspected for a century. A software system is written in a few years and patched for decades. Some historians have argued that this imbalance in attention is not merely an oversight of storytelling but a distortion with real consequences. Societies that celebrate innovation and treat upkeep as drudgery tend to underfund it, and the costs of deferred maintenance accumulate quietly until they surface as failures — a collapsed overpass, a water system contaminated by corroded pipes, a bank whose core software no living employee fully understands.

The bias toward novelty is reinforced by how institutions reward work. Engineers are promoted for launching new products rather than for extending the life of old ones; politicians cut ribbons at new stations but gain little credit for repainting old ones. Maintenance is, almost by definition, invisible when it succeeds: its achievement is that nothing happens. This makes it hard to measure and easy to cut. None of this means innovation is unimportant. The point is rather that a society's technological capacity depends as much on its ability to sustain what it has built as on its ability to build something new. To take maintenance seriously would require changing not only budgets but status — recognising the mechanic, the inspector and the systems administrator as custodians of a shared inheritance rather than as minor figures in someone else's story.`,
    question: "The primary purpose of the passage is to:",
    options: [
      "A. argue that innovation is overrated and should receive less funding than it does.",
      "B. propose a reliable method for measuring the value of maintenance work.",
      "C. trace the history of specific infrastructure failures caused by poor design.",
      "D. argue that the neglect of maintenance is a consequential bias that deserves correction."
    ],
    answer: "D"
  },
  {
    id: 2,
    type: "mcq",
    passageId: "rc1",
    question: "The statement that maintenance is “invisible when it succeeds” implies that:",
    options: [
      "A. maintenance workers prefer to remain anonymous.",
      "B. most maintenance efforts ultimately fail.",
      "C. the costs of maintenance cannot be estimated in advance.",
      "D. successful maintenance produces no visible event for which anyone can be credited."
    ],
    answer: "D"
  },
  {
    id: 3,
    type: "mcq",
    passageId: "rc1",
    question: "Which of the following, if true, would most weaken the author's claim about how institutions reward work?",
    options: [
      "A. In most large engineering firms, promotion criteria weight the reliability record of existing systems as heavily as new product launches.",
      "B. New railway stations cost far more to build than repainting old ones.",
      "C. Some infrastructure failures are caused by design flaws rather than poor upkeep.",
      "D. Nineteenth-century historians wrote more about inventors than about mechanics."
    ],
    answer: "A"
  },
  {
    id: 4,
    type: "mcq",
    passageId: "rc1",
    question: "The author would most likely agree with which of the following?",
    options: [
      "A. Maintenance should always receive a larger budget than innovation.",
      "B. The contributions of inventors have been exaggerated in every history of technology.",
      "C. Software systems are harder to maintain than physical infrastructure.",
      "D. A country that builds many new railways but neglects its existing ones may see its technological capacity decline."
    ],
    answer: "D"
  },
  {
    id: 5,
    type: "mcq",
    passageId: "rc2",
    passage: `Before the spread of mechanical clocks, most people measured time by tasks rather than by hours. A fisherman's day was organised by the tides, a farmer's by daylight and the season, a cook's by how long it took rice to boil. Time in this sense was not an abstract quantity to be spent or saved; it was inseparable from the activity it measured. The historian E. P. Thompson called this “task orientation” and argued that the shift to timed labour was one of the deepest transformations of the industrial age. In the factory, the employer purchased not a finished product but the worker's time, and so time itself had to be measured, divided and disciplined. Bells, clocks and fines for lateness taught a new habit of mind in which an idle hour was a loss.

It would be easy to romanticise the older rhythm, but task orientation had its own hardships: when the harvest demanded it, labour could stretch without limit, and periods of slack brought no income. What changed was not simply the amount of work but its relationship to the person doing it. Under timed labour, the worker's day was split into time that belonged to the employer and time that belonged to oneself — a division that later made possible both the eight-hour-day movement and the modern idea of “leisure”. Paradoxically, then, the clock that enforced discipline also created the very category of free time that workers would later fight to expand. Today, as digital work dissolves fixed hours once again, some observers see a return to task orientation. Whether this is liberation or a new form of discipline may depend on who sets the tasks.`,
    question: "As described in the passage, “task orientation” refers to a way of life in which:",
    options: [
      "A. time is understood through the activities being performed.",
      "B. time is valued as a scarce resource to be saved.",
      "C. workers are paid according to the number of tasks completed.",
      "D. employers assign a fixed set of tasks each day."
    ],
    answer: "A"
  },
  {
    id: 6,
    type: "mcq",
    passageId: "rc2",
    question: "The author says “it would be easy to romanticise the older rhythm” in order to:",
    options: [
      "A. argue that pre-industrial life was harsher than factory life.",
      "B. suggest that farmers worked fewer hours than factory workers.",
      "C. criticise E. P. Thompson's account of the industrial age.",
      "D. acknowledge that the pre-industrial mode of work was not free of difficulties."
    ],
    answer: "D"
  },
  {
    id: 7,
    type: "mcq",
    passageId: "rc2",
    question: "The “paradox” mentioned in the passage is that:",
    options: [
      "A. a tool of workplace discipline also gave rise to the notion of time owned by the worker.",
      "B. workers fought for shorter hours while employers wanted longer ones.",
      "C. clocks made work more efficient but less productive.",
      "D. leisure existed long before the invention of mechanical clocks."
    ],
    answer: "A"
  },
  {
    id: 8,
    type: "mcq",
    passageId: "rc2",
    question: "The final sentence of the passage suggests that:",
    options: [
      "A. digital work is certain to free workers from discipline.",
      "B. a return to task orientation is impossible in modern economies.",
      "C. fixed working hours should be restored for digital workers.",
      "D. the significance of a return to task-based work depends on who controls that work."
    ],
    answer: "D"
  },
  {
    id: 9,
    type: "mcq",
    passageId: "rc3",
    passage: `In 1995 the fisheries scientist Daniel Pauly described what he called “shifting baseline syndrome”. Each generation of scientists, he observed, accepts as natural the condition of fish stocks at the start of its own career and measures decline from that point. Over several generations the baseline drifts downward, and a sea that has lost most of its large fish can come to seem normal. The concept has since travelled far beyond fisheries; similar effects have been described in how people perceive bird populations, forest cover and even the size of fish in old holiday photographs.

The syndrome is partly a problem of memory and partly a problem of data. Long-term records are scarce, and where they exist they are often anecdotal — a ship's log, a market price list, a fisherman's diary. Scientists trained to trust controlled measurements have sometimes been reluctant to use such sources. Yet without them, assessments of “healthy” populations may be anchored to conditions that were already severely degraded. Some researchers have responded by turning to historical ecology, combining archival documents, archaeological remains and oral histories to reconstruct earlier states of ecosystems.

These reconstructions raise a difficult question for conservation: if the baseline has shifted, which past should restoration aim for? A pre-industrial state may be impossible to recover in a changed climate, and choosing any particular date involves value judgements as much as science. Recognising the syndrome does not settle such questions, but it does guard against the most comfortable error — mistaking the world we inherited for the world as it must be.`,
    question: "According to the passage, shifting baseline syndrome occurs because:",
    options: [
      "A. each generation treats the conditions it first observes as the natural reference point.",
      "B. fish populations recover faster than scientists expect.",
      "C. scientists deliberately ignore evidence of ecological decline.",
      "D. long-term records consistently overstate past abundance."
    ],
    answer: "A"
  },
  {
    id: 10,
    type: "mcq",
    passageId: "rc3",
    question: "Scientists have sometimes been reluctant to use ships' logs and fishermen's diaries because such sources:",
    options: [
      "A. are too recent to be useful.",
      "B. are written in languages scientists cannot read.",
      "C. always contradict archaeological evidence.",
      "D. lack the rigour of controlled measurement."
    ],
    answer: "D"
  },
  {
    id: 11,
    type: "mcq",
    passageId: "rc3",
    question: "The “most comfortable error” referred to in the last sentence is:",
    options: [
      "A. assuming that present ecological conditions represent an unaltered natural state.",
      "B. believing that pre-industrial ecosystems can easily be restored.",
      "C. relying too heavily on oral histories.",
      "D. assuming that climate change has no effect on ecosystems."
    ],
    answer: "A"
  },
  {
    id: 12,
    type: "mcq",
    passageId: "rc3",
    question: "Which of the following can be inferred from the passage?",
    options: [
      "A. Historical ecology has fully solved the problem of shifting baselines.",
      "B. Restoration should always aim for the pre-industrial state of an ecosystem.",
      "C. Shifting baselines affect only the study of marine life.",
      "D. Choosing a target for ecological restoration cannot be settled by scientific data alone."
    ],
    answer: "D"
  },
  {
    id: 13,
    type: "mcq",
    passageId: "rc4",
    passage: `It is often said that some words are untranslatable — the Portuguese saudade, the German Schadenfreude, the Japanese wabi-sabi. Such lists are popular because they flatter two intuitions at once: that languages shape distinct ways of seeing, and that one's own language has gaps that others have filled. Translators tend to be more sceptical. In practice, almost any word can be rendered into another language if one is willing to use a phrase instead of a single word, or to let context do part of the work. What resists translation is less the isolated word than the web of associations around it — the songs in which it appears, the situations in which it would be natural to say it, the tone it carries in a particular community.

This suggests that the popular framing gets the problem backwards. The difficulty is not that a word has no equivalent, but that it has too many partial ones, each capturing some features and distorting others. A translator choosing among them is not looking up a definition but making an interpretive decision, one that depends on the purpose of the text and its intended readers. A legal contract, a poem and a children's book may each require a different rendering of the same term.

Seen this way, translation is not a failed attempt at perfect equivalence but a form of writing in its own right. The translator is an author who works under unusually strict constraints. That status has been slow to win recognition: translators' names are still frequently omitted from book covers, as though the text had passed from one language to another untouched by human judgement.`,
    question: "The author's attitude towards popular lists of “untranslatable” words is that they:",
    options: [
      "A. misidentify where the real difficulty in translation lies.",
      "B. accurately identify the hardest words to translate.",
      "C. should be used to train professional translators.",
      "D. prove that languages shape distinct ways of seeing."
    ],
    answer: "A"
  },
  {
    id: 14,
    type: "mcq",
    passageId: "rc4",
    question: "According to the author, the popular framing “gets the problem backwards” because:",
    options: [
      "A. context makes translation unnecessary.",
      "B. most words have exact equivalents in other languages.",
      "C. translators prefer phrases to single words.",
      "D. the challenge stems from an abundance of imperfect equivalents rather than a lack of any."
    ],
    answer: "D"
  },
  {
    id: 15,
    type: "mcq",
    passageId: "rc4",
    question: "The example of a legal contract, a poem and a children's book is used to illustrate that:",
    options: [
      "A. some genres are impossible to translate.",
      "B. poems are harder to translate than contracts.",
      "C. translators should specialise in a single genre.",
      "D. the appropriate translation of a term depends on the purpose and audience of the text."
    ],
    answer: "D"
  },
  {
    id: 16,
    type: "mcq",
    passageId: "rc4",
    question: "The final sentence of the passage implies that:",
    options: [
      "A. publishers deliberately hide translation errors.",
      "B. translators prefer to remain anonymous.",
      "C. omitting translators' names reflects an undervaluing of the interpretive work they do.",
      "D. books should be published only in their original language."
    ],
    answer: "C"
  },
  {
    id: 17,
    type: "tita",
    question: "The four sentences (labelled 1, 2, 3, 4) below, when properly sequenced, form a coherent paragraph. Key in the sequence of the four numbers as your answer.\n1. Yet the same feature that makes them efficient also makes them fragile.\n2. Modern supply chains are designed to keep inventories as lean as possible.\n3. A single delayed shipment can therefore halt production across several countries.\n4. Holding little stock reduces storage costs and frees up capital.",
    answer: "2413"
  },
  {
    id: 18,
    type: "tita",
    question: "The four sentences (labelled 1, 2, 3, 4) below, when properly sequenced, form a coherent paragraph. Key in the sequence of the four numbers as your answer.\n1. The answer, surprisingly, had less to do with rainfall than with soil.\n2. Researchers set out to understand why two neighbouring valleys with nearly identical climates supported such different forests.\n3. In one valley, centuries of grazing had compacted the ground, preventing roots from reaching deeper water.\n4. In the other, the loose, undisturbed earth allowed trees to survive long dry spells.",
    answer: "2134"
  },
  {
    id: 19,
    type: "tita",
    question: "The four sentences (labelled 1, 2, 3, 4) below, when properly sequenced, form a coherent paragraph. Key in the sequence of the four numbers as your answer.\n1. This is why early versions of a product are often deliberately incomplete.\n2. Startups frequently face a choice between perfecting a product and releasing it quickly.\n3. Releasing early allows a company to learn what customers actually value.\n4. Perfection, by contrast, risks investing heavily in features nobody wants.",
    answer: "2341"
  },
  {
    id: 20,
    type: "mcq",
    question: "The passage given below is followed by four summaries. Choose the option that best captures the essence of the passage.\nPublic libraries are often discussed as if their only function were to lend books, and declining loan figures are taken as evidence of their obsolescence. But libraries today serve as one of the few indoor public spaces where people can stay without being expected to buy anything. They offer internet access to those without it, help with job applications, and quiet space for students in crowded homes. Judging them by loans alone misses most of what they now do.",
    options: [
      "A. Evaluating libraries only by book loans overlooks their wider and evolving social role.",
      "B. Libraries should stop lending books and focus on internet access.",
      "C. Libraries are becoming obsolete because fewer books are borrowed.",
      "D. Libraries are the only public spaces where people can stay without spending money."
    ],
    answer: "A"
  },
  {
    id: 21,
    type: "mcq",
    question: "The passage given below is followed by four summaries. Choose the option that best captures the essence of the passage.\nStudies of expert chess players show that their advantage lies less in calculating more moves than in recognising patterns. Shown a position from a real game for a few seconds, masters can reconstruct it almost perfectly, whereas novices cannot. But when pieces are placed randomly, masters perform barely better than novices. Expertise, it seems, is built on a large stored library of meaningful configurations rather than on a superior general memory.",
    options: [
      "A. Chess masters have better memories than novices in all situations.",
      "B. Chess expertise rests mainly on recognising familiar patterns, not on a generally superior memory.",
      "C. Novices can become masters by memorising random positions.",
      "D. Calculation plays no role in chess."
    ],
    answer: "B"
  },
  {
    id: 22,
    type: "mcq",
    question: "The passage given below is followed by four summaries. Choose the option that best captures the essence of the passage.\nCities that ban cars from their centres often face fierce opposition from local shopkeepers, who fear losing customers. Yet in many cases footfall and retail sales in pedestrianised streets have risen after the change, as people linger longer and visit more shops. The fears are understandable, since the costs are immediate and visible while the benefits arrive gradually.",
    options: [
      "A. Pedestrianisation always increases retail sales.",
      "B. Shopkeepers' opposition to car-free zones is irrational and should be ignored.",
      "C. Car-free zones often benefit retailers, despite initial fears rooted in the timing of costs and benefits.",
      "D. Customers generally prefer to shop by car."
    ],
    answer: "C"
  },
  {
    id: 23,
    type: "tita",
    question: "Five jumbled sentences (labelled 1, 2, 3, 4, 5), related to a topic, are given below. Four of them can be put together to form a coherent paragraph. Identify the odd sentence and key in the number of that sentence as your answer.\n1. Bees communicate the location of food through a movement known as the waggle dance.\n2. The angle of the dance relative to the vertical indicates the direction of the food source relative to the sun.\n3. Honey has been used as a natural preservative since ancient times.\n4. The duration of the waggle portion signals how far away the source is.\n5. Other bees follow the dancer closely to read these cues.",
    answer: "3"
  },
  {
    id: 24,
    type: "tita",
    question: "Five jumbled sentences (labelled 1, 2, 3, 4, 5), related to a topic, are given below. Four of them can be put together to form a coherent paragraph. Identify the odd sentence and key in the number of that sentence as your answer.\n1. Inflation erodes the purchasing power of money held as cash.\n2. Savers therefore look for assets whose returns exceed the rate at which prices rise.\n3. Central banks raise interest rates partly to make such saving more attractive.\n4. Many ancient coins were minted from gold and silver.\n5. When real returns turn negative, people are more inclined to spend or borrow instead.",
    answer: "4"
  }
];

export default catMock2Varc;
